import { getBomTemplate } from "@/api/template";
import BomCreator from "@/components/BomCreator/BomCreator";
import FileDownloader from "@/components/FileDownloader/FileDownloader";
import Skeleton from "@/components/Skeleton/Skeleton";
import TableHeader from "@/components/TableHeader/TableHeader";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import useTranslation from "@/hooks/useTranslation";
import {
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleFilled,
  ExportOutlined,
  FileAddOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, message, Popconfirm, Space, Tooltip } from "antd";
import { useParams } from "react-router-dom";
import UploadProductAction from "../../actions/UploadProductAction/UploadProductAction";
import {
  useDeleteBom,
  useDeleteProxy,
  useExportProductsBom,
  useGetProductBOM,
} from "../../useProducts";
import styles from "./BillOfMaterials.module.scss";
import BillOfMaterialsTable from "./BillOfMaterialsTable";

export default function BillOfMaterials() {
  useTrackOnLoad("BOM Viewed");

  const { productId: id } = useParams();
  const productId = Number(id);
  const { data, isLoading } = useGetProductBOM({ productId });
  const bom = data?.bom;
  const product = data?.product;

  const exportProduct = useExportProductsBom();
  const deleteProxy = useDeleteProxy();
  const deleteBom = useDeleteBom();

  const translation = useTranslation();
  const isProxy = product?.relation.includes("proxy");
  const bomAvailable = (data?.bom.children || []).length > 0;

  return (
    <div className={styles.BillOfMaterials}>
      <div>
        {!isProxy && (
          <TableHeader
            noRoundedTop
            left={
              isLoading ? (
                <>
                  <Skeleton style={{ height: "32px", width: "230px" }} />
                  <Skeleton style={{ height: "14px", width: "80px" }} />
                </>
              ) : (
                product &&
                bom && (
                  <>
                    <Space.Compact>
                      <UploadProductAction productId={productId}>
                        <Button>
                          <UploadOutlined /> Upload
                        </Button>
                      </UploadProductAction>
                      <BomCreator productId={productId}>
                        <Button>
                          <FileAddOutlined /> Create
                        </Button>
                      </BomCreator>
                      <FileDownloader
                        tooltipText="Download template"
                        fileOutputName={`template-product-boms.xlsx`}
                        fileUrlFn={getBomTemplate}
                        buttonText={<DownloadOutlined />}
                      />
                      <FileDownloader
                        tooltipText="Export"
                        fileOutputName={`export-products.xlsx`}
                        fileUrlFn={() =>
                          exportProduct
                            .mutateAsync({ productIds: [bom.id] })
                            .then((data) => data.url)
                        }
                        buttonText={<ExportOutlined />}
                        addTimestampAndCompany={true}
                      />
                      <Popconfirm
                        title={"Delete bill of materials"}
                        disabled={!bomAvailable}
                        icon={<ExclamationCircleFilled style={{ color: "#F5222D" }} />}
                        styles={{ body: { maxWidth: "400px" } }}
                        description={
                          <>
                            <p>
                              Deleting the bill of materials only involves deleting structure of
                              this product (and its proxies), but does not delete the materials.
                            </p>

                            <p>This action can not be undone.</p>
                          </>
                        }
                        okText={"Delete"}
                        okType={"primary"}
                        okButtonProps={{
                          danger: true,
                          loading: deleteBom.isLoading || deleteProxy.isLoading,
                          disabled: deleteBom.isLoading || deleteProxy.isLoading,
                        }}
                        onConfirm={() =>
                          deleteBom
                            .mutateAsync({ productId: product.id })
                            .then((result) => {
                              if (result.status === 200) {
                                message.success("BOM deleted.");
                              }
                            })
                            .catch(() => message.error(translation.errors.general))
                        }
                      >
                        <Tooltip title={"Delete BOM"}>
                          <Button
                            style={{ color: "red" }}
                            disabled={!bomAvailable}
                          >
                            <DeleteOutlined />
                          </Button>
                        </Tooltip>
                      </Popconfirm>
                    </Space.Compact>
                  </>
                )
              )
            }
            right={
              isLoading ? (
                <Skeleton
                  style={{
                    width: "65px",
                    height: "20px",
                  }}
                />
              ) : (
                <span className={styles.total}>{data?.total_count} total</span>
              )
            }
          />
        )}
        <div className={styles.bom}>
          <BillOfMaterialsTable
            data={data?.bom}
            loading={isLoading}
            noDataMessage="No bill of materials available for this product. Upload a bill of materials or add a proxy."
          />
        </div>
      </div>
    </div>
  );
}
